import { createSelector } from 'reselect'

import {
	getUsersTimeLocation,
	transformAvailablePeriodsToArray,
} from '@app/helpers'
import {
	CoachSystemAvailability,
	CoachSystemData,
	CoachSystemDataContact,
	CoachSystemDataTierCoach,
	EngagementStatus,
	PATIENT_PLANS,
} from '@app/types/coachSystemData'
import { RootState } from '@app/store'
import { PatientData } from '@app/types/patients'
import { SystemConfigCountryData } from '@app/types/systemConfig'
import { selectTimeFormatter } from '@app/hooks/contexts/timeController/formatters'

import { selectPatientPhone } from '../darioSystemData/selectors'

const executeFullAddressLine = (
	contactData: CoachSystemDataContact,
	countries: Array<SystemConfigCountryData>
): string => {
	const countryId = contactData?.country ? `${contactData?.country},` : ''
	const countryState = contactData?.state ? `${contactData?.state},` : ''
	const addressLine1 = contactData?.addressLine1
		? `${contactData?.addressLine1},`
		: ''
	const addressLine2 = contactData?.addressLine2
		? `${contactData?.addressLine2},`
		: ''
	const zipCode = contactData?.zipCode || ''
	const city = contactData?.city ? `${contactData?.city},` : ''

	const countryData = countryId
		? countries.find(c => +c.id === +countryId)
		: null

	const countryName = countryData?.name?.toUpperCase() || ''

	return `${addressLine1} ${addressLine2} ${city} ${countryState} ${countryName} ${zipCode}`
}

export const selectUserAvatar = (state: RootState): string =>
	state.appReducer.patientOverview?.coachSystemData?.imageUrl || ''

export const selectPatientFirstName = (state: RootState): string =>
	state.appReducer.patientOverview?.coachSystemData?.firstName || ''

export const selectPatientLastName = (state: RootState): string =>
	state.appReducer.patientOverview?.coachSystemData?.lastName || ''

export const selectPatientName = (state: RootState): string => {
	const firstName =
		state.appReducer.patientOverview?.coachSystemData?.firstName

	const lastName = state.appReducer.patientOverview?.coachSystemData?.lastName

	const email = state.appReducer.patientOverview?.coachSystemData?.email

	if (firstName && lastName) return `${firstName} ${lastName}`

	if (!firstName && lastName) return lastName

	if (firstName && !lastName) return firstName

	if (!firstName && !lastName && email) return email

	return ''
}

export const selectAddressLine1 = (state: RootState): string =>
	state.appReducer.patientOverview?.coachSystemData?.contact?.addressLine1 ||
	''

export const selectAddressLine2 = (state: RootState): string =>
	state.appReducer.patientOverview?.coachSystemData?.contact?.addressLine2 ||
	''

export const selectCity = (state: RootState): string =>
	state.appReducer.patientOverview?.coachSystemData?.contact?.city || ''

export const selectCountry = (state: RootState): string =>
	state.appReducer.patientOverview?.coachSystemData?.contact?.country || '0'

export const selectState = (state: RootState): string =>
	state.appReducer.patientOverview?.coachSystemData?.contact?.state || ''

export const selectPatientZipCode = (state: RootState): string =>
	state.appReducer.patientOverview?.coachSystemData?.contact?.zipCode || ''

/**
 * Shipping address selectors
 */

export const selectAddressShippingLine1 = (state: RootState): string =>
	state.appReducer.patientOverview?.coachSystemData?.contactShipping
		?.addressLine1 || ''

export const selectAddressShippingLine2 = (state: RootState): string =>
	state.appReducer.patientOverview?.coachSystemData?.contactShipping
		?.addressLine2 || ''

export const selectShippingCity = (state: RootState): string =>
	state.appReducer.patientOverview?.coachSystemData?.contactShipping?.city ||
	''

export const selectShippingCountry = (state: RootState): string =>
	state.appReducer.patientOverview?.coachSystemData?.contactShipping
		?.country || '0'

export const selectShippingState = (state: RootState): string =>
	state.appReducer.patientOverview?.coachSystemData?.contactShipping?.state ||
	''

export const selectPatientShippingZipCode = (state: RootState): string =>
	state.appReducer.patientOverview?.coachSystemData?.contactShipping
		?.zipCode || ''

export const selectShortPatientAddress = (state: RootState): string => {
	const countries: Array<SystemConfigCountryData> =
		state.systemConfig.system.countries
	const contactData =
		state.appReducer.patientOverview?.coachSystemData?.contact
	const countryId = contactData?.country
	const countryState = contactData?.state
	const city = contactData?.city ? `${contactData?.city},` : ''

	const countryData = countryId
		? countries.find(c => +c.id === +countryId)
		: null

	const countryName = countryData?.name || ''

	const countryStateAbbreviation = countryData?.states.find(
		state => countryState === state.name
	)?.abbreviation

	const countryStateAbbreviationText = countryStateAbbreviation
		? `${countryStateAbbreviation},`
		: ''

	return contactData
		? `${city} ${countryStateAbbreviationText} ${countryName.toUpperCase()}`
		: ''
}

export const selectBillingAddress = (state: RootState): string => {
	const countries: Array<SystemConfigCountryData> =
		state.systemConfig.system.countries

	const contactData =
		state.appReducer.patientOverview?.coachSystemData?.contact

	return contactData ? executeFullAddressLine(contactData, countries) : ''
}

export const selectShippingAddress = (state: RootState): string => {
	const countries: Array<SystemConfigCountryData> =
		state.systemConfig.system.countries

	const contactData =
		state.appReducer.patientOverview?.coachSystemData?.contactShipping

	return contactData ? executeFullAddressLine(contactData, countries) : ''
}

export const selectMemberSince = (state: RootState): string => {
	const timeLocale = getUsersTimeLocation(
		state.appReducer?.user?.localization?.dateConfiguration
	)

	const dateFormatter = selectTimeFormatter(timeLocale, 'df')

	const startOfService =
		state.appReducer.patientOverview?.coachSystemData?.startOfService

	return startOfService ? dateFormatter(startOfService) : ''
}

export const selectAccount = (state: RootState): string =>
	state.appReducer.patientOverview?.coachSystemData?.account || ''

export const selectProduct = (state: RootState): any =>
	state.appReducer.patientOverview?.coachSystemData?.product || []

export const selectPatientId = (state: RootState): string =>
	state.appReducer.patientOverview?.coachSystemData?._id || ''

const selectPatientCoaches = (state: RootState): CoachSystemData | undefined =>
	state.appReducer.patientOverview?.coachSystemData

export const selectPatientCoachesData = createSelector(
	selectPatientCoaches,
	coaches => {
		const coachDataFieldsKeys = ['tier1Coach', 'tier2Coach', 'tier3Coach']

		const assignedCoachesArr: Array<CoachSystemDataTierCoach> = []

		if (!coaches) return assignedCoachesArr

		for (const key in coaches) {
			if (coachDataFieldsKeys.includes(key) && coaches[key].coachId) {
				assignedCoachesArr.push(coaches[key])
			}
		}

		return assignedCoachesArr
	}
)

export const selectPatientCoachingMinutesData = (state: RootState): number => {
	return (
		state.appReducer.patientOverview?.coachSystemData?.engagementData
			?.coachingMinutes || 0
	)
}

export const selectPatientMeasurements = (state: RootState): number =>
	state.appReducer.patientOverview?.coachSystemData?.engagementData
		?.measurements || 0

export const selectLastActiveContact = (state: RootState): string =>
	state.appReducer.patientOverview?.coachSystemData?.engagementData
		?.lastActiveContect || ''

export const selectPatientBillableActivity = (state: RootState): string =>
	state.appReducer.patientOverview?.coachSystemData?.engagementData
		?.billableActivity || ''

export const selectAvailableOptions = (
	state: RootState
): CoachSystemAvailability =>
	state.appReducer.patientOverview?.coachSystemData?.availability || {}

export const selectAvailableHours = createSelector(
	selectAvailableOptions,
	availabilityOptions => {
		const availableHours = {
			noon: availabilityOptions.noon,
			morning: availabilityOptions.morning,
			afternoon: availabilityOptions.afternoon,
			evening: availabilityOptions.evening,
		}

		return transformAvailablePeriodsToArray(availableHours)
	}
)

export const selectAvailableDays = (state: RootState): Array<string> => {
	return (
		state.appReducer.patientOverview?.coachSystemData?.availability?.days ||
		[]
	)
}

export const selectPatientAlternativePhone = (state: RootState): string =>
	state.appReducer.patientOverview?.coachSystemData?.contact
		?.alternativePhone || ''

export const selectPatientPhones = createSelector(
	[selectPatientPhone, selectPatientAlternativePhone],
	(
		phone,
		alternativePhone
	): Array<{
		id: string
		phone: string
	}> => {
		const phones: Array<{
			id: string
			phone: string
		}> = []

		if (phone) phones.push({ id: 'first phone', phone })

		if (alternativePhone)
			phones.push({ id: 'second phone', phone: alternativePhone })

		return phones
	}
)

export const selectPatientEmployment = (state: RootState): string => {
	return (
		state.appReducer.patientOverview?.coachSystemData?.personal
			?.employment || ''
	)
}

export const selectPatientEmail = (state: RootState): string =>
	state.appReducer.patientOverview?.coachSystemData?.email || ''

export const selectPatientID = (state: RootState): string => {
	return state.appReducer.patientOverview?.coachSystemData?.ID || ''
}

export const selectMaritalStatus = (state: RootState): string =>
	state.appReducer.patientOverview?.coachSystemData?.personal
		?.maritalStatus || ''

export const selectPatientAttentions = (state: RootState): string =>
	state.appReducer.patientOverview?.coachSystemData?.attentions || ''

export const selectContactMethods = (state: RootState): Array<string> =>
	state.appReducer.patientOverview?.coachSystemData?.availability?.methods ||
	[]

export const selectUnscheduled = (state: RootState): boolean =>
	state.appReducer.patientOverview?.coachSystemData?.availability
		?.unscheduled || false

export const selectShortPatientData = createSelector(
	[selectPatientID, selectPatientId, selectPatientEmail],
	(ID, id, email): PatientData | undefined => {
		if (!ID || !id || !email) return

		return { _id: id, ID, email }
	}
)

export const selectEngagementStatus = (state: RootState): EngagementStatus =>
	state.appReducer.patientOverview?.coachSystemData?.engagementData
		?.engagementStatus

export const selectSmokeStatus = (state: RootState): string =>
	state.appReducer?.patientOverview?.coachSystemData?.personal?.smoking || '0'

export const selectMSKPlanStatusForPatient = (state: RootState): boolean => {
	const plans =
		state.appReducer?.patientOverview?.coachSystemData?.engagementData
			?.plan || []

	return plans.includes(PATIENT_PLANS.MSK)
}
